import { useState, type FormEvent } from "react";
import { useLocation } from "react-router";
import { post } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useIsDesktop } from "../lib/device";
import { recentErrors } from "../lib/errlog";
import { runningBundle } from "../lib/freshness";
import { ErrorLine, Sheet, useToast } from "./ui";

const MAX_CHARS = 4000; // the API's MAX_ANSWER_CHARS

/**
 * File a bug (2026-10-15): a round button with a bug at the bottom right of
 * every signed-in page opens a sheet with three questions: what were you
 * doing, what did you see, how can we reproduce it. Only "What did you see?"
 * must be answered; Send says so if it is empty. With the answers go this page's path, the bundle the tab
 * runs, the window size and language, and the last errors the tab saw
 * (lib/errlog.ts); the API adds the browser and its own release. Operators
 * read the reports at /admin/bugs.
 *
 * Signed out there is no button: the API takes reports from a session only.
 * The button sits under sheets and the full-screen player (z-index 15), and on
 * the phone above the sticky bar at the bottom of the page (`--foot-h`, set
 * by components/Shell.tsx). What was typed stays when the sheet is closed
 * and goes when the report is sent.
 */
export function BugButton() {
  const { me } = useAuth();
  const loc = useLocation();
  const desktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const [doing, setDoing] = useState("");
  const [saw, setSaw] = useState("");
  const [reproduce, setReproduce] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [toast, say] = useToast();
  // none for a Player Spotlight beta participant, whose app is the questionnaire alone
  if (!me || (me.user.access === "beta" && !me.user.is_operator)) return null;

  // a message about the last Send goes once the user changes what it was about
  const edit = (set: (v: string) => void, v: string) => {
    set(v);
    setErr(null);
  };
  const close = () => {
    if (busy) return;
    setOpen(false);
    setErr(null);
  };
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!saw.trim()) {
      setErr(new Error("Write what you saw, then send."));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await post("/v1/bug-reports", {
        doing,
        saw,
        reproduce,
        page: `${loc.pathname.replace(/^\/claim\/[^/]+/, "/claim/[redacted]")}${loc.search}`,
        web_build: runningBundle(),
        context: {
          origin: window.location.origin,
          viewport: [window.innerWidth, window.innerHeight],
          dpr: window.devicePixelRatio,
          language: navigator.language,
          errors: recentErrors(),
        },
      });
      setDoing("");
      setSaw("");
      setReproduce("");
      setOpen(false);
      say("Report sent. Thank you.");
    } catch (e) {
      setErr(e as Error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button type="button" className="bugfab" onClick={() => setOpen(true)} aria-label="Report a bug" title="Report a bug" data-testid="bug-btn">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 7.6V6.5a3 3 0 0 1 6 0v1.1" />
            <path d="M9.6 4.2L8 2.8M14.4 4.2L16 2.8" />
            <rect x="7" y="7.5" width="10" height="13" rx="5" />
            <path d="M12 11v9.4" />
            <path d="M7 11.5H3.5M7 15.5H3M7.6 19l-3 2.2M17 11.5h3.5M17 15.5h4M16.4 19l3 2.2" />
          </svg>
        </button>
      )}
      {open && (
        <Sheet onClose={close}>
          <form className="bugform" onSubmit={send} data-testid="bug-sheet">
            <h2>Report a bug</h2>
            <label className="mt16" htmlFor="bug-doing">What were you doing?</label>
            <textarea id="bug-doing" className="field ta" rows={2} maxLength={MAX_CHARS} value={doing} onChange={(e) => edit(setDoing, e.target.value)} placeholder="For example: uploading a match video" autoFocus={desktop} />
            <label className="mt16" htmlFor="bug-saw">What did you see?</label>
            <textarea id="bug-saw" className="field ta" rows={3} maxLength={MAX_CHARS} value={saw} onChange={(e) => edit(setSaw, e.target.value)} placeholder="The message or what went wrong, in your words" required />
            <label className="mt16" htmlFor="bug-reproduce">How can we reproduce it?</label>
            <textarea id="bug-reproduce" className="field ta" rows={3} maxLength={MAX_CHARS} value={reproduce} onChange={(e) => edit(setReproduce, e.target.value)} placeholder="The steps, in order, if you know them" />
            <p className="tiny mt10">Sent with it: this page's address, your browser, the app version and the last errors the app saw.</p>
            <ErrorLine error={err} />
            <div className="mt16"><button type="submit" className="btn dark full" disabled={busy} data-testid="bug-send">{busy ? "Sending…" : "Send report"}</button></div>
            <div className="mt10"><button type="button" className="btn ghost full" onClick={close} disabled={busy}>Cancel</button></div>
          </form>
        </Sheet>
      )}
      {toast}
    </>
  );
}
